import { useMemo } from 'react';
import type { BusinessPartnerWithStats, Address } from '@/api/business-partner/business-partner.types';

const fmtEUR = (v: number | null) =>
  v == null
    ? '—'
    : new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(v);

const fmtCompact = (v: number) => {
  if (v >= 1_000_000) return `€${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1_000) return `€${(v / 1_000).toFixed(1)}k`;
  return `€${v}`;
};

interface BpHeaderProps {
  bp: BusinessPartnerWithStats;
  addresses: Address[];
  ordersTotal: number;
}

export function BpHeader({ bp, addresses, ordersTotal }: BpHeaderProps) {
  const faviconUrl = useMemo(() => {
    if (!bp.website) return null;
    try {
      const domain = new URL(
        bp.website.startsWith('http') ? bp.website : `https://${bp.website}`,
      ).hostname;
      return `https://www.google.com/s2/favicons?domain=${domain}&sz=32`;
    } catch {
      return null;
    }
  }, [bp.website]);

  const typeLabel =
    bp.type === 'cCustomer'
      ? 'Kunde'
      : bp.type === 'cSupplier'
        ? 'Lieferant'
        : 'Kunde & Lieferant';

  const addrCount = addresses.length;

  return (
    <>
      <div className="bp-detail-header">
        <div className="bp-detail-title-row">
          <span className="bp-detail-id">{bp.id}</span>
          <span className="bp-detail-name">
            {bp.legalName || bp.name}
            {faviconUrl && (
              <img src={faviconUrl} alt="" className="bp-detail-favicon" />
            )}
          </span>
          <span className="bp-detail-type">{typeLabel}</span>
        </div>
        {(bp.email || bp.website) && (
          <div className="bp-detail-links">
            {bp.email && (
              <a href={`mailto:${bp.email}`} className="bp-quick-link">
                ✉ {bp.email}
              </a>
            )}
            {bp.website && (
              <a
                href={
                  bp.website.startsWith('http')
                    ? bp.website
                    : `https://${bp.website}`
                }
                target="_blank"
                rel="noreferrer"
                className="bp-quick-link"
              >
                ↗ {bp.website}
              </a>
            )}
          </div>
        )}
      </div>
      <div className="bp-metrics">
        <Metric
          label="Umsatz"
          value={bp.revenue > 0 ? fmtCompact(bp.revenue) : '—'}
          cls={bp.revenue > 0 ? undefined : 'muted'}
          highlight
        />
        <Metric label="Aufträge" value={String(ordersTotal)} />
        <Metric
          label="Offen"
          value={String(bp.openOrders)}
          cls={bp.openOrders > 0 ? 'warn' : 'muted'}
        />
        <Metric
          label="Kreditlimit"
          value={bp.creditLimit ? fmtEUR(bp.creditLimit) : '—'}
          cls={!bp.creditLimit ? 'muted' : undefined}
        />
        <Metric label="Adressen" value={String(addrCount)} />
      </div>
    </>
  );
}

function Metric({
  label,
  value,
  cls,
  highlight,
}: {
  label: string;
  value: string;
  cls?: string;
  highlight?: boolean;
}) {
  return (
    <div className={`bp-metric${highlight ? ' highlight' : ''}`}>
      <div className="label">{label}</div>
      <div className={`value${cls ? ` ${cls}` : ''}`}>{value}</div>
    </div>
  );
}
